import { Box, HStack, Stack, Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import { type ReactElement, useCallback, useMemo } from 'react'

import { isNotNull } from '@/utils/helpers'
import parseQueryNumber from '@/utils/parseQueryNumber'
import toEnumValueOrNull from '@/utils/toEnumValueOrNull'

import LearnerFeedbackQ from '@/queries/LearnerFeedbackQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'
import RPC from '@/client/rpc'
import {
  DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS,
  QUERY_REFLECTION_SESSION_ID,
} from '@/client/services/dialogues/constants'
import useDialoguesReflectionFormInputs from '@/client/services/formInputs/useDialoguesReflectionFormInputs'
import {
  LEARNER_FEEDBACK_TYPE_DIALOGUES,
  LearnerFeedbackAttributeTag,
} from '@/client/services/learnerFeedback/constants'
import type { SubmitLearnerFeedbacksItem } from '@/client/services/learnerFeedback/types'

import LearnerFeedbackDisplay from '@/components/LearnerFeedback/LearnerFeedbackDisplay'
import Spinner from '@/components/Spinner'
import FormWizardV2 from '@/components/UI/FormWizardV2/FormWizardV2'
import type { FormWizardV2Values } from '@/components/UI/FormWizardV2/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

function isStringArray(val: unknown): val is string[] {
  return Array.isArray(val) && val.every(v => typeof v === 'string')
}
const items: SubmitLearnerFeedbacksItem[] = []

export default function DialoguesReflectionModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}): ReactElement | null {
  const query = useQuery()
  const router = useRouter()
  const me = useUser()

  const sessionId = parseQueryNumber({
    value: router.query[QUERY_REFLECTION_SESSION_ID],
    defaultValue: 0,
  })
  const session = query.one(SessionQ.where({ id: sessionId }))
  const sessionEndDateISO = session(SessionQ.getComputedEndDate())
  const topic = session.one(SessionQ.getTopic())
  const topicName = topic(TopicQ.name)
  const title = `Session Reflection${topicName != null ? `: ${topicName}` : ''}`

  const isExpired = useMemo(() => {
    if (sessionEndDateISO == null) {
      return false
    }

    return (
      DateTime.fromISO(sessionEndDateISO) <
      DateTime.now().minus({ days: DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS })
    )
  }, [sessionEndDateISO])

  const partnerCount = session == null ? null : session(SessionQ.countConversationPartners(me.id))

  const allPartners = useMemo(
    () => session.many(SessionQ.getConversationPartners(me.id)),
    [session, me.id]
  )

  const selfFeedbacks =
    session?.many(
      LearnerFeedbackQ.where(
        {
          sessionId: SessionQ.id,
          reviewerUserId: me.id,
          type: LEARNER_FEEDBACK_TYPE_DIALOGUES,
        },
        SQL`${LearnerFeedbackQ.selfReflectionFilter(true)}`
      )
    ) ?? []

  const peerFeedbacks =
    session?.many(
      LearnerFeedbackQ.where(
        {
          sessionId: SessionQ.id,
          reviewerUserId: me.id,
          type: LEARNER_FEEDBACK_TYPE_DIALOGUES,
        },
        SQL`${LearnerFeedbackQ.selfReflectionFilter(false)}`
      )
    ) ?? []

  const dialoguesFeedbacks = [...selfFeedbacks, ...peerFeedbacks]

  const userHasCompletedReflection = dialoguesFeedbacks.length > 0

  const { steps } = useDialoguesReflectionFormInputs({
    allPartners,
  })

  const handleSubmit = useCallback(
    async (values: FormWizardV2Values<string>) => {
      const peerItems = allPartners
        .map(partner => {
          const id = partner(UserQ.id) ?? 0
          if (id === 0) {
            return null
          }
          const stepValues = values[`peer-${id}`] ?? {}
          const rawTagsValue = stepValues[`peer-${id}-tags`]
          const rawTags = isStringArray(rawTagsValue) ? rawTagsValue : []
          const attributeTags = rawTags
            .map(tag => toEnumValueOrNull(LearnerFeedbackAttributeTag, tag))
            .filter(isNotNull)
          const commentValue = stepValues[`peer-${id}-comment`]
          const comment = typeof commentValue === 'string' ? commentValue.trim() : ''
          const hasContent = comment.length > 0 || attributeTags.length > 0
          return hasContent
            ? ({
                sessionId,
                revieweeUserId: id,
                comment,
                attributeTags,
                type: LEARNER_FEEDBACK_TYPE_DIALOGUES,
              } satisfies SubmitLearnerFeedbacksItem)
            : null
        })
        .filter(isNotNull)

      if (peerItems.length > 0) {
        items.push(...peerItems)
      }

      const selfValues = values['self'] ?? {}
      const selfCommentValue = selfValues['self-comment']
      const selfComment = typeof selfCommentValue === 'string' ? selfCommentValue.trim() : ''
      if (selfComment.length > 0) {
        items.push({
          sessionId,
          revieweeUserId: me.id,
          comment: selfComment,
          attributeTags: [],
          type: LEARNER_FEEDBACK_TYPE_DIALOGUES,
        })
      }

      if (items.length > 0) {
        await RPC.submitLearnerFeedbacks(items)
      }

      onClose()
    },
    [allPartners, me.id, onClose, sessionId]
  )

  if (!isOpen || (isExpired && !userHasCompletedReflection) || sessionId === 0) {
    return null
  }

  return (
    <ModalV3
      open={isOpen}
      onClose={onClose}
      size="3xl"
      disableClickOutside
      title={title}
      body={() => {
        if (userHasCompletedReflection) {
          return (
            <Box maxHeight="60vh" overflowY="auto" pr="8px">
              <Stack spacing="16px">
                <Text fontSize="md">Thank you for completing the reflection for this session!</Text>
                {dialoguesFeedbacks.map(dialoguesFeedback => (
                  <LearnerFeedbackDisplay
                    key={`${dialoguesFeedback(LearnerFeedbackQ.id)}`}
                    feedback={dialoguesFeedback}
                    showRecipient
                    showType
                  />
                ))}
              </Stack>
            </Box>
          )
        }

        if (partnerCount == null) {
          return (
            <HStack>
              <Spinner />
              <Text>Finding conversation partners...</Text>
            </HStack>
          )
        }

        if (partnerCount === 0) {
          return <Text>No conversation partners found for this session</Text>
        }

        return (
          <FormWizardV2
            name="dialogues-reflection"
            checklistType="horizontal-stepper"
            steps={steps}
            maxWidth="100%"
            onSubmit={handleSubmit}
          />
        )
      }}
    />
  )
}
